import { type BaseContext } from '@atlassian/state-machine/types';
import { nanoid } from 'nanoid';

// todo: https://product-fabric.atlassian.net/browse/CNS-10273 - Remove dependency
// eslint-disable-next-line no-restricted-imports
import { currentSessionId } from '@canvas/canvas-state/analytics';

import { type RemoteDocument } from '../connection';
import { type WsSocketError } from '../network/errors';
import { type Queue } from '../network/queue';
import { type RelaySupplierResult } from '../network/socket';
import { type Transport, type TransportFactory } from '../transport';

import type { State } from './network-state-machine';
import {
  type AuthenticatedEvent,
  type SyncFetchingEvent,
} from './network-state-machine-events';

export interface Debounce {
  debounceReconnection(): Promise<void>;
  resetAttempts(): void;
  captureError(error: Error): void;
  captureErrorState(state: State): void;
  readonly attempt: number;
  readonly onlineAttempts: number;
}

export interface PendingMessage {
  message: Uint8Array<ArrayBufferLike>;
  enqueue: boolean;
}

export interface SocketMachineContext extends BaseContext {
  remoteDocument?: RemoteDocument;
  relayDetails?: RelaySupplierResult;
  transport?: Transport;
  transportFactory: TransportFactory;
  error?: Error | WsSocketError;
  sessionId?: string;
  traceId: string;
  debounce: Debounce;

  signal: AbortSignal;
  pendingMessages: Queue<PendingMessage>;

  socketFetchSnapshot(signal: AbortSignal): Promise<RemoteDocument>;
  socketFetchSteps(
    remoteDocument: RemoteDocument,
    attempt: number,
    signal: AbortSignal
  ): Promise<RemoteDocument>;
  socketApplyChanges(relayUrl: string, remoteDoc: RemoteDocument): RemoteDocument;
  socketUpdateChanges(
    remoteDocument: RemoteDocument,
    connectionAttempts: number,
    signal: AbortSignal
  ): void;
  reconcileOfflineChanges?(remoteDocument: RemoteDocument): Promise<void>;
  socketConnect?(): void;
  relaySupplier(attempt: number, signal: AbortSignal): Promise<RelaySupplierResult>;
  shouldHaltRetry(): boolean;
  shouldSkipUpdates(): boolean;
}

export function captureError<T extends Pick<SocketMachineContext, 'error'>>(
  context: T,
  event: { error: WsSocketError | Error }
) {
  context.error = event.error;
  return context;
}

export function captureRelayDetails<T extends Pick<SocketMachineContext, 'relayDetails'>>(
  context: T,
  event: AuthenticatedEvent
) {
  context.relayDetails = event.relayDetails;
  return context;
}

export function generateTraceId<T extends Pick<SocketMachineContext, 'traceId'>>(
  context: T
) {
  context.traceId = nanoid();
  return context;
}

export function captureSessionId<T extends Pick<SocketMachineContext, 'sessionId'>>(
  context: T
) {
  context.sessionId = currentSessionId.sessionId;
  return context;
}

export function captureDocumentDetails<
  T extends Pick<SocketMachineContext, 'relayDetails' | 'remoteDocument'>
>(context: T, event: SyncFetchingEvent) {
  context.relayDetails = event.relayDetails;
  context.remoteDocument = event.remoteDocument;
  return context;
}
